<template>
  <div>
    <SectionSeparatorWrapper v-if="isQuoteRefinement" :separator="false">
      <!-- Ask if current members and redirect - JOL -->
      <h3 class="h6 sub-heading-underlined">{{ sectionHeader }}</h3>
      <template v-if="refData.healthInsuranceDefenceHealthMemberHelpText">
        <FormField :labelText="formLabels.currentMemberLabel">
          <ul class="radio-group large">
            <li v-for="option in [...isCurrentMemberOptions]" :key="`defence-health-member-${parent}-${option.display}`">
              <input
                type="radio"
                :id="`defenceHealthMember-${parent}-${option.display}`"
                :value="option.returnValue"
                v-model="currentDefenceMember"
                @change="updateCurrentMember"
              />
              <label :for="`defenceHealthMember-${parent}-${option.display}`">{{ option.display }}</label>
            </li>
          </ul>
          <Alert v-if="currentDefenceMember" alertType="error" class="inline-message">
            <p v-html="isPartner ? refData.healthInsuranceDefenceHealthPartnerHelpText.informationText : refData.healthInsuranceDefenceHealthMemberHelpText.informationText" />
          </Alert>
        </FormField>
      </template>
    </SectionSeparatorWrapper>
    <SectionSeparatorWrapper :separator="false" v-if="areAnyAbdQuestionsShown || areAnyLhcQuestionsShown">
      <!-- ABD Sections -->
      <template v-if="areAnyAbdQuestionsShown">
        <h3 v-if="!isQuoteRefinement" class="h6 sub-heading-underlined">{{ sectionHeader }} - age based discount</h3>
        <!-- Age based discount (ABD) -->
        <template v-if="showABDFirstTimeQuestion">
          <div class="form-section">
            <FormField :labelText="formLabels.isFirstTimeLabel" required>
              <ul class="radio-group large">
                <li v-for="option in isFirstTimeOptions" :key="`first-time-owning-insurance-${parent}-${option.display}`">
                  <input
                    type="radio"
                    :id="`first-time-owning-insurance-${parent}-${option.display}`"
                    :value="option.returnValue"
                    :name="'is-owning-health-insurance-first-time' + parent + option.display"
                    v-model="v$.abd.isFirstTime.$model"
                  />
                  <label :for="`first-time-owning-insurance-${parent}-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>

            <FormField v-if="abd.isFirstTime === false" :labelText="formLabels.isReceivingABDLabel" required :helperText="refData.currentlyReceivingABDHelpText.informationText">
              <ul class="radio-group large">
                <li v-for="option in receivingABDOptions" :key="`receiving-ABD-${parent}-${option.returnValue}`">
                  <input
                    type="radio"
                    :id="`receiving-ABD-${parent}-${option.returnValue}`"
                    :value="option.returnValue"
                    :name="'receiving-ABD' + parent"
                    v-model="v$.abd.currentlyReceivingABD.$model"
                  />
                  <label :for="`receiving-ABD-${parent}-${option.returnValue}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>

            <FormField v-if="abd.isFirstTime === false && abd.currentlyReceivingABD" :labelText="formLabels.selectCdaLabel" required>
              <DropdownSelect :initialSelect="findCdaDropdown()" :items="cdaDropdownOptions" v-model="abd.selectedCdaDropdown" @change="updateCdaValue" />
            </FormField>

            <!-- this section is only shown if policy holder/partner age 18 to 29 -->
            <FormField v-if="showYourCDA" :labelText="formLabels.showCdaLabel" :required="false" :helperText="yourCDAHelperText">
              <p>{{ currentCDA }}</p>
            </FormField>
          </div>
        </template>
        <!-- Read-only ABD section -->
        <template v-if="readOnlyABD && (calculatedCDA || existingAgeBasedDiscount)">
          <FormField v-if="calculatedCDA" labelText="Your certified discount age is" :required="false" :helperText="yourCDAHelperText">
            <p>{{ calculatedCDA }}</p>
          </FormField>

          <FormField v-if="existingAgeBasedDiscount" labelText="Age based discount" :required="false">
            <p>{{ existingAgeBasedDiscount }}%</p>
          </FormField>
        </template>
      </template>

      <!-- Lifetime health cover (LHC) -->
      <template v-if="areAnyLhcQuestionsShown">
        <h3 v-if="!isQuoteRefinement" class="h6 sub-heading-underlined">{{ sectionHeader }} - lifetime health cover</h3>
        <!-- LHC section - CAE & Updated absent days edit mode -->
        <div v-if="reapplyingHospitalEditableLHC">
          <FormField labelText="Certified age of entry" required>
            <input type="number" placeholder="Enter certified age of entry" disabled :value="existingLhc" />
          </FormField>
          <FormField labelText="Updated absent days" :required="true" placeholder="Enter number of absent days">
            <input type="number" v-model="v$.lhc.totalAbsentDays.$model" disabled />
          </FormField>
        </div>
        <!-- Read-only LHC section -->
        <div v-else-if="readOnlyLHC && activeLHC" class="form-section">
          <FormField v-if="existingCertifiedAgeOfEntry" labelText="Certified age of entry" :required="false">
            <p>{{ existingCertifiedAgeOfEntry }}</p>
          </FormField>

          <FormField v-if="!isEmptyString(existingLhcLoadingRate)" labelText="LHC loading" :required="false">
            <p>{{ existingLhcLoadingRate }}%</p>
          </FormField>
        </div>
        <!-- New LHC form -->
        <div v-else-if="!readOnlyLHC" class="form-section">
          <FormField :labelText="formLabels.lhcSituationLabel" required>
            <DropdownSelect :initialSelect="situationDropdownInitialSelect" :items="lhcDropdownOptions" v-model="lhc.situation"></DropdownSelect>
            <p v-if="lhc.situation !== undefined && lhcDropdownSupportText" style="padding-top: 0.5rem" v-html="lhcDropdownSupportText" />
            <template v-if="showChangeCoverLhcAlertMessage">
              <Alert :message="refData.appliedLoadingsInformationText.informationText" alertType="error" />
            </template>
          </FormField>

          <FormField
            v-if="isCitizenOrMigrant && !showChangeCoverLhcAlertMessage"
            :labelText="`${formLabels.lhcCitizenMigrantPartialLabel} ${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'return to Australia' : 'register for Medicare'}?`"
            required
          >
            <FormInput
              placeholder="DD / MM / YYYY"
              v-maska="'## / ## / ####'"
              class="form-field half-width no-margin-bottom"
              type="text"
              :id="`lhc-${parent}-${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}`"
              v-model.trim="v$.lhc[`${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}Date`].$model"
              maxLength="14"
              :isDateInput="true"
              :overrideClass="v$.lhc[`${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}Date`].$error ? 'error' : null"
            />
            <label
              v-if="v$.lhc[`${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}Date`].$error"
              :for="`lhc-${parent}-${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}`"
              class="error"
            >
              {{ getDateErrorMessage(v$.lhc[`${lhc.situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}Date`]) }}
            </label>
            <template v-if="lhc.situation === LHC_SITUATION_VALUES.MIGRANT && refData.lhcMigrantHelpText" v-slot:helperText>
              <div v-html="refData.lhcMigrantHelpText.informationText" />
            </template>
          </FormField>

          <template v-if="lhc.situation === LHC_SITUATION_VALUES.EX_ADF && !showChangeCoverLhcAlertMessage">
            <FormField
              v-for="situationType in ['enlist', 'discharge']"
              :key="`${situationType} date for lhc ${parent}`"
              :labelText="`${formLabels.lhcExADFPartialLabel} ${situationType}?`"
              required
            >
              <FormInput
                class="form-field half-width no-margin-bottom"
                placeholder="DD / MM / YYYY"
                v-maska="'## / ## / ####'"
                situationType="text"
                :id="`lhc-${parent}-${situationType}`"
                v-model.trim="v$.lhc[`${situationType}Date`].$model"
                maxLength="14"
                :isDateInput="true"
                :overrideClass="v$.lhc[`${situationType}Date`].$error ? 'error' : null"
              />
              <label v-if="v$.lhc[`${situationType}Date`].$error" :for="`lhc-${parent}-${situationType}`" class="error">
                {{ getDateErrorMessage(v$.lhc[`${situationType}Date`], situationType) }}
              </label>
            </FormField>
          </template>

          <template v-if="lhc.situation === LHC_SITUATION_VALUES.CURRENT_HOSPITAL_COVER">
            <FormField class="half-width" :labelText="formLabels.currentlyHoldHospitalCoverLabel" :helperText="refData.firstReceivePHIAgeHelpText.informationText" required>
              <input
                type="number"
                id="firstTimePHIAge"
                v-model="v$.lhc.cae.$model"
                placeholder="Enter age"
                name="first time PHI age"
                step="1"
                :min="v$.lhc.cae.minValue.$params.min"
                :max="v$.lhc.cae.maxValue.$params.max"
              />
              <label for="certifiedAgeOfEntry" class="error" v-if="caeFieldError">{{ caeFieldError }}</label>
            </FormField>
          </template>
        </div>
      </template>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import { required, requiredIf, minValue, maxValue, integer } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { SectionSeparatorWrapper, FormField, FormInput, Alert } from '@/components';
import { DATE_VALIDATOR } from '@general/js/validators';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { calculateAge, calculateCertifiedDiscountAge, isPastDate, isPastDateIncludingToday, reverseFormatFormDate } from '@general/js/utils/dateTimeUtils';
import { EMIT_KEYS } from '@general/js/utils/JoinNow/JoinNowUtils';
import { beforeYear1970, isDateBefore, isPastDateExcludingToday, lessThan90Days, overPastDate, isEmptyString, isLhcReasonExemptFromCae } from '@general/js/utils';
import ProductTypeEnum from '@general/js/enums/ProductTypeEnum';
import { ProductTypeCodes, LHCOverruleReasons as LHC_SITUATION_VALUES } from '@general/js/enums';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'HealthInsuranceHistorySection',

  components: {
    SectionSeparatorWrapper,
    FormField,
    FormInput,
    DropdownSelect,
    Alert,
  },

  props: {
    eligibleForABD: {
      type: Boolean,
      required: true,
    },

    products: {
      type: Array,
      required: true,
    },

    refData: {
      type: Object,
      required: true,
    },

    /**
     * String of cover start date in "dd / mm / yyyy"
     */
    coverStartDate: {
      type: String,
      required: true,
    },

    /**
     * String of date of birth in "dd / mm / yyyy"
     */
    dob: {
      type: String,
      required: true,
    },

    portalAgeBasedDiscount: {
      type: Object,
      default: null,
    },

    /**
     * Boolean to determine if member previously had health insurance or not.
     * true if had before
     */
    memberStatus: {
      type: Boolean,
      default: undefined,
    },

    initialData: {
      type: Object,
      required: true,
    },

    /**
     * String to determine which parent class is using this component
     */
    parentClass: {
      type: String,
      required: true,
    },

    lastSection: {
      type: Boolean,
      default: false,
    },

    /**
     * Boolean to not display LHC under any circumstances (in cases of change cover)
     */
    doNotDisplayLhc: {
      type: Boolean,
      default: false,
    },

    /**
     * Section Header
     * */
    sectionHeader: {
      type: String,
      default: 'Health insurance history',
    },

    /**
     * Boolean to show 3-step Quote Refinement related questions / logic - default is component is not in 3-step Quote Refinement
     */
    isQuoteRefinement: {
      type: Boolean,
      default: false,
    },

    /**
     * Is the form being asked from Change Cover flow (MP / CRM)
     */
    isChangeCover: {
      type: Boolean,
      default: false,
    },

    readOnlyLHC: {
      type: Boolean,
      default: false,
    },

    readOnlyABD: {
      type: Boolean,
      default: false,
    },

    existingLhcEditMode: {
      type: Boolean,
      default: false,
    },

    existingLhc: {
      type: Number,
      default: null,
    },

    isPartner: {
      type: Boolean,
      default: false,
    },
  },

  created() {
    const initialData = this.initialData;

    // Retrieve cover details in Change Cover uses ProductTypeCode rather than type/productType from CRM recommendations
    if (this.isChangeCover) {
      this.containsHospital = this.products.some((product) => {
        return product.productTypeCode === ProductTypeCodes.HOSPITAL_ONLY || product.productTypeCode === ProductTypeCodes.PACKAGE;
      });
    } else {
      const productTypes = this.products.map((product) => {
        return product.type ? product.type.toLowerCase() : product.productType.toLowerCase();
      });

      this.containsHospital = productTypes.some((type) => {
        return type === ProductTypeEnum.HOSPITAL_ONLY || type === ProductTypeEnum.PACKAGE;
      });
    }

    if (initialData.lhc?.overruledReason) {
      this.lhc.situation = initialData.lhc.overruledReason;

      switch (initialData.lhc.overruledReason) {
        case LHC_SITUATION_VALUES.CITIZEN:
          this.lhc.citizenDate = initialData.lhc.date;
          break;

        case LHC_SITUATION_VALUES.MIGRANT:
          this.lhc.migrantDate = initialData.lhc.date;
          break;

        case LHC_SITUATION_VALUES.EX_ADF:
          this.lhc.enlistDate = initialData.lhc.date;
          this.lhc.dischargeDate = initialData.lhc.dischargeDate;
          break;

        case LHC_SITUATION_VALUES.CURRENT_HOSPITAL_COVER:
          this.lhc.cae = initialData.lhc.cae;
          break;

        default:
          break;
      }
    }
  },

  mounted() {
    this.$emit(EMIT_KEYS.LHC_ELIGIBILITY_UPDATED, this.eligibleForLHC);
    this.loaded = true;
    /**
     * dynamically add watcher for lhc.situation and eligibleForLHC
     * this is to avoid unwanted reset in lhc response when there are exiting data for lhc
     */
    this.$watch('lhc.situation', (newValue, oldValue) => {
      if (oldValue !== newValue) {
        this.v$.lhc.situation?.$touch();
      }

      if (this.loaded) {
        this.lhc.citizenDate = '';
        this.lhc.migrantDate = '';
        this.lhc.enlistDate = '';
        this.lhc.dischargeDate = '';
        this.lhc.cae = undefined;
      }
    });

    this.$watch('eligibleForLHC', (bool) => {
      if (this.loaded) {
        this.lhc = {
          situation: undefined,
          citizenDate: '',
          migrantDate: '',
          enlistDate: '',
          dischargeDate: '',
          cae: undefined,
        };
      }

      this.$emit(EMIT_KEYS.LHC_ELIGIBILITY_UPDATED, bool);
    });
  },

  data() {
    return {
      loaded: false,
      LHC_SITUATION_VALUES,
      isFirstTimeOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: 'No' },
      ],
      receivingABDOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: "No or I don't know" },
      ],
      isCurrentMemberOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: 'No' },
      ],
      abd: {
        // eslint-disable-next-line no-nested-ternary
        isFirstTime: this.portalAgeBasedDiscount?.firstTimePHI ?? (this.initialData?.abd?.isFirstTime !== undefined ? this.initialData?.abd?.isFirstTime : this.memberStatus),
        currentlyReceivingABD: this.portalAgeBasedDiscount?.currentlyReceived ?? (undefined || this.initialData?.abd?.currentlyReceivingABD),
        cda: this.portalAgeBasedDiscount?.cda ?? (undefined || this.initialData?.abd?.cda),
        selectedCdaDropdown: undefined || this.initialData?.abd?.selectedCdaDropdown,
      },
      lhc: {
        situation: this.initialData?.lhc?.overruledReason || undefined,
        citizenDate: '',
        migrantDate: '',
        enlistDate: '',
        dischargeDate: '',
        cae: this.initialData.lhc?.cae ? this.initialData.lhc.cae : null,
        totalAbsentDays: '0',
      },
      containsHospital: null,
      parent: this.parentClass,
      DOB: this.dob,
      currentDefenceMember: undefined || this.initialData?.currentDefenceMember,
    };
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    situationDropdownInitialSelect() {
      return this.lhc.situation !== undefined
        ? this.lhcDropdownOptions.find((obj) => {
            return obj.value === this.lhc.situation;
          }).name
        : 'Select your situation';
    },

    isCitizenOrMigrant() {
      return [LHC_SITUATION_VALUES.CITIZEN, LHC_SITUATION_VALUES.MIGRANT].includes(this.lhc.situation);
    },

    eligibleForLHC() {
      if ((this.containsHospital && !this.doNotDisplayLhc) || this.isChangeCover) {
        // eligible for LHC if age based on cover start date is 31 or more
        const ageAtCoverStartDate = calculateAge(this.dob, this.coverStartDate);
        return ageAtCoverStartDate >= 31;
      }

      return false;
    },

    validationObj() {
      const obj = {};

      // Validation for ABD should only applied when eligible and able to edit
      if (this.eligibleForABD && !this.readOnlyABD && !this.portalAgeBasedDiscount?.cda) {
        obj.abd = {
          isFirstTime: { required },
          currentlyReceivingABD: {
            required: requiredIf(function () {
              return this.abd.isFirstTime === false;
            }),
          },
          cda: {
            required: requiredIf(function () {
              return this.abd.isFirstTime === false && this.abd.currentlyReceivingABD === true;
            }),
          },
        };
      }
      if (this.eligibleForLHC && this.reapplyingHospitalEditableLHC) {
        obj.lhc = {
          totalAbsentDays: {
            required,
            integer,
            minValue: minValue(0),
          },
        };
      } else if (this.eligibleForLHC && !this.readOnlyLHC) {
        // Validation for LHC should only applied when eligible and able to edit
        obj.lhc = {
          situation: { required },
        };

        if (this.isChangeCover) {
          obj.lhc.situation = {
            isInvalid(overruleReason) {
              return !isLhcReasonExemptFromCae(overruleReason);
            },
          };
        }

        const lhcSituation = this.lhc.situation;
        if ([LHC_SITUATION_VALUES.CITIZEN, LHC_SITUATION_VALUES.MIGRANT].includes(lhcSituation)) {
          obj.lhc[`${lhcSituation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}Date`] = {
            required,
            DATE_VALIDATOR,
            isPastDate,
          };
        }

        if (lhcSituation === LHC_SITUATION_VALUES.EX_ADF) {
          obj.lhc.enlistDate = {
            required,
            DATE_VALIDATOR,
            isPastDateExcludingToday,
            overPastDate,
          };

          obj.lhc.dischargeDate = {
            required,
            DATE_VALIDATOR,
            lessThan90Days,
            beforeYear1970,
            beforeEnlistment(value) {
              return isDateBefore(value, this.lhc.enlistDate);
            },
          };
        }

        if (lhcSituation === LHC_SITUATION_VALUES.CURRENT_HOSPITAL_COVER) {
          obj.lhc.cae = {
            required,
            dateRange(value) {
              return value >= 30 && value <= calculateAge(this.dob);
            },
            integer,
            minValue: minValue(30),
            maxValue: maxValue(125),
          };
        }
      }

      return obj;
    },

    showChangeCoverLhcAlertMessage() {
      return !this.readOnlyLHC && this.eligibleForLHC && this.isChangeCover && isLhcReasonExemptFromCae(this.lhc.situation);
    },

    caeFieldError() {
      if (this.v$.lhc.cae.$error) {
        if (this.v$.lhc.cae.minValue.$invalid || this.v$.lhc.cae.maxValue.$invalid) {
          return 'Certified age of entry should be between 30 and 125. If you fall outside this range, select another option that best represents your situation.';
        }
        if (this.v$.lhc.cae.integer.$invalid) {
          return 'Enter a valid integer between 30 and 125. Please remove any decimal points.';
        }
        if (this.v$.lhc.cae.required.$invalid) {
          return 'This is a required field';
        }
        if (this.v$.lhc.cae.dateRange.$invalid) {
          return 'Age must be your current age or younger';
        }
      }
      // Return null if no error so that it can be converted to boolean to determine if error message should be shown
      return null;
    },

    totalAbsentDaysFieldError() {
      if (this.v$.lhc.totalAbsentDays.$error) {
        if (this.v$.lhc.totalAbsentDays.minValue.$invalid) {
          return 'Absent days cannot be less than 0';
        }
        if (this.v$.lhc.totalAbsentDays.integer.$invalid) {
          return 'Please enter a valid integer';
        }
        if (this.v$.lhc.totalAbsentDays.required.$invalid) {
          return 'This is a required field';
        }
      }
      // Return null if no error so that it can be converted to boolean to determine if error message should be shown
      return null;
    },

    showYourCDA() {
      const age = calculateAge(this.dob, this.coverStartDate); // age as of cover start date
      const ageInRange = age >= 18 && age <= 29;

      return ageInRange && (this.abd.isFirstTime || (this.abd.isFirstTime === false && this.abd.currentlyReceivingABD === false) || this.abd.cda !== undefined);
    },

    currentCDA() {
      if (!Number.isNaN(Number(this.abd.cda)) && this.abd.currentlyReceivingABD && this.abd.isFirstTime === false) {
        return Number(this.abd.cda);
      }

      return calculateCertifiedDiscountAge(this.dob, this.coverStartDate);
    },

    cdaDropdownOptions() {
      const ref = this.refData.firstReceiveAgeBasedDiscountOptions;
      const items = [];

      for (let i = ref.minimum; i <= ref.maximum; i++) {
        const obj = { name: String(i) };
        if (i <= calculateAge(this.dob)) {
          items.push(obj);
        }
      }
      items.push({ name: ref.other });

      return items;
    },

    yourCDAHelperText() {
      if (!this.abd.isFirstTime && this.abd.currentlyReceivingABD) {
        const cdaKnown = this.abd.cda >= 18 && this.abd.cda <= 29 && this.abd.selectedCdaDropdown !== this.refData.firstReceiveAgeBasedDiscountOptions.other;
        return this.refData[cdaKnown ? 'currentlyReceivingABDWithAgeHelpText' : 'currentlyReceivingABDWithoutAgeHelpText'].informationText;
      }

      return this.refData[this.abd.isFirstTime ? 'healthInsuranceFirstTimeHelpText' : 'currentlyReceivingABDNoHelpText'].informationText;
    },

    lhcDropdownOptions() {
      const ref = this.refData.lhcOptions;
      let options = [];

      ref.forEach((option) => {
        options.push({
          name: option.display,
          value: option.returnValue,
          supportText: option.supportText,
        });
      });

      // if eligible for ABD and is first time having public health insurance, hide `held phi / 31 yo` option
      if (this.abd.isFirstTime && this.eligibleForABD) {
        options = options.filter((obj) => {
          return obj.value !== LHC_SITUATION_VALUES.HELD_PHI_31_YO && obj.value !== LHC_SITUATION_VALUES.CURRENT_HOSPITAL_COVER;
        });
      }

      return options;
    },

    showABDFirstTimeQuestion() {
      // If Quote Refinement page, then additional check of if prospect is current / past Defence Health Member (will get referred based on refData and callout)
      if (this.isQuoteRefinement) {
        return this.currentDefenceMember === false && this.eligibleForABD;
      }

      return this.eligibleForABD && !this.readOnlyABD && !this.portalAgeBasedDiscount?.cda;
    },

    lhcDropdownSupportText() {
      return this.lhcDropdownOptions.find((obj) => {
        return obj.value === this.lhc.situation;
      }).supportText;
    },

    formLabels() {
      const policyHolderLabels = {
        currentMemberLabel: 'Are you a Defence Health member today?',
        isFirstTimeLabel: 'Are you taking out your own private health insurance for the first time?',
        isReceivingABDLabel: 'Are you currently receiving an age-based-discount?',
        selectCdaLabel: 'At what age did you first receive an age-based discount?',
        showCdaLabel: 'Your certified discount age is',
        lhcSituationLabel: 'Please select the option that best represents your situation:',
        lhcCitizenMigrantPartialLabel: 'When did you',
        lhcExADFPartialLabel: 'What date did you',
        currentlyHoldHospitalCoverLabel: 'At what age did you first take out private health insurance?',
      };

      const partnerLabels = {
        currentMemberLabel: 'Is your partner a Defence Health member today?',
        isFirstTimeLabel: 'Is your partner taking out their own private health insurance for the first time?',
        isReceivingABDLabel: 'Is your partner currently receiving an age-based-discount?',
        selectCdaLabel: 'At what age did your partner first receive an age-based discount?',
        showCdaLabel: "Your partner's certified discount age is",
        lhcSituationLabel: "Please select the option that best represents your partner's situation:",
        lhcCitizenMigrantPartialLabel: 'When did your partner',
        lhcExADFPartialLabel: 'What date did your partner',
        currentlyHoldHospitalCoverLabel: 'At what age did your partner first take out private health insurance?',
      };

      return this.isPartner ? partnerLabels : policyHolderLabels;
    },

    activeLHC() {
      if (this.initialData.lhc?.startDate && this.initialData.lhc?.cae) {
        return isPastDateIncludingToday(reverseFormatFormDate(this.initialData.lhc?.startDate));
      }
      return false;
    },

    existingCertifiedAgeOfEntry() {
      if (this.initialData.lhc?.cae && this.activeLHC) {
        return this.initialData.lhc?.cae;
      }
      return null;
    },

    existingLhcLoadingRate() {
      if (!isEmptyString(this.initialData.lhc?.lhcLoadingRate) && this.activeLHC) {
        return this.initialData.lhc?.lhcLoadingRate;
      }
      return null;
    },

    existingAgeBasedDiscount() {
      if (this.initialData.abd?.ageBasedDiscount) {
        return this.initialData.abd?.ageBasedDiscount;
      }
      return null;
    },

    calculatedCDA() {
      // CDA should be either the existing value or their current age if aged between 18-29
      // Value will only be emitted if readOnlyABD is true
      const age = calculateAge(this.dob, this.coverStartDate);
      if (this.initialData.abd?.cda) {
        return this.initialData.abd?.cda;
      }
      if (age >= 18 && age <= 29) {
        return calculateCertifiedDiscountAge(this.dob, this.coverStartDate);
      }
      return null;
    },

    areAnyAbdQuestionsShown() {
      return this.eligibleForABD && (this.isQuoteRefinement || this.showABDFirstTimeQuestion || (this.readOnlyABD && (this.calculatedCDA || this.existingAgeBasedDiscount)));
    },

    areAnyLhcQuestionsShown() {
      return this.eligibleForLHC && ((this.readOnlyLHC && this.activeLHC) || !this.readOnlyLHC);
    },

    reapplyingHospitalEditableLHC() {
      return this.existingLhcEditMode && this.existingLhc;
    },
  },

  watch: {
    'abd.currentlyReceivingABD': {
      handler() {},
    },

    'abd.isFirstTime': {
      handler(newVal, oldVal) {
        if (this.loaded) {
          if (
            (this.lhc.situation === LHC_SITUATION_VALUES.HELD_PHI_31_YO || this.lhc.situation === LHC_SITUATION_VALUES.CURRENT_HOSPITAL_COVER) &&
            oldVal === false &&
            newVal === true
          ) {
            this.lhc.situation = undefined;
          }

          this.abd.currentlyReceivingABD = undefined;
        }
      },
    },

    dob: {
      handler() {
        this.DOB = this.dob;
        this.abd.cda = undefined; // undefined so that currentCda runs a new cda calculation
        this.abd.selectedCdaDropdown = undefined;
      },
    },

    coverStartDate: {
      handler() {
        this.abd.cda = this.currentCDA;
      },
    },

    $data: {
      handler(data) {
        if (this.loaded) {
          const isCDACalculated = data.abd.cda === this.cdaDropdownOptions[this.cdaDropdownOptions.length - 1].name;
          const situation = data.lhc.situation;
          let cda = data.abd.cda !== undefined ? Number(data.abd.cda) : undefined;

          if (isCDACalculated || data.abd.isFirstTime === true || data.abd.currentlyReceivingABD === false) {
            cda = this.currentCDA;
          }

          // recalculates cda if 'I don't know' option is selected to update when dob value is changed
          if (
            data.abd.isFirstTime === false &&
            data.abd.currentlyReceivingABD === true &&
            data.abd.selectedCdaDropdown === this.refData.firstReceiveAgeBasedDiscountOptions.other
          ) {
            cda = this.currentCDA;
          }

          const dataToEmit = {
            abd: {
              isFirstTime: data.abd.isFirstTime,
              currentlyReceivingABD: data.abd.isFirstTime === true ? null : data.abd.currentlyReceivingABD,
              cda: this.readOnlyABD ? this.calculatedCDA : cda,
              selectedCdaDropdown: data.abd.selectedCdaDropdown,
            },
            lhc: {
              cae: data.lhc.cae || 30, // TODO: for now, `cae` is hardcoded to be `30`. make it editable is out of scope.
              overruledReason: situation,
              dischargeDate: undefined,
            },
            invalid: this.v$.$invalid,
            dirty: this.v$.$dirty,
          };

          switch (situation) {
            case LHC_SITUATION_VALUES.CITIZEN:
            case LHC_SITUATION_VALUES.MIGRANT:
              dataToEmit.lhc.date = data.lhc[`${situation === LHC_SITUATION_VALUES.CITIZEN ? 'citizen' : 'migrant'}Date`];
              break;

            case LHC_SITUATION_VALUES.EX_ADF:
              dataToEmit.lhc.date = data.lhc.enlistDate;
              dataToEmit.lhc.dischargeDate = data.lhc.dischargeDate;
              break;

            case LHC_SITUATION_VALUES.CURRENT_HOSPITAL_COVER:
              dataToEmit.lhc.cae = data.lhc.cae;
              break;

            case LHC_SITUATION_VALUES.NONE:
              if (this.isChangeCover) {
                delete dataToEmit.lhc.cae;
              } else {
                dataToEmit.lhc.cae = 0;
              }
              delete dataToEmit.lhc.date;
              delete dataToEmit.lhc.dischargeDate;
              break;

            default:
              break;
          }

          if (!this.eligibleForABD) {
            dataToEmit.abd = null;
          } else if (!isEmptyString(this.initialData.abd?.ageBasedDiscount)) {
            dataToEmit.abd.ageBasedDiscount = this.initialData.abd.ageBasedDiscount;
          }

          if (!this.eligibleForLHC) {
            dataToEmit.lhc = null;
          } else {
            if (this.initialData.lhc?.startDate) {
              dataToEmit.lhc.startDate = this.initialData.lhc.startDate;
            }
            if (!isEmptyString(this.initialData.lhc?.lhcLoadingRate)) {
              dataToEmit.lhc.lhcLoadingRate = this.initialData.lhc.lhcLoadingRate;
            }
            if (this.reapplyingHospitalEditableLHC) {
              dataToEmit.lhc = {};
              dataToEmit.lhc.totalAbsentDays = data.lhc.totalAbsentDays;
            }
          }

          this.$emit(EMIT_KEYS.ABD_LHC_UPDATED, dataToEmit);
        }
      },
      deep: true,
    },
  },

  methods: {
    getDateErrorMessage(input, dateType) {
      if (input.$invalid) {
        let dateTypeText;

        switch (this.lhc.situation) {
          case LHC_SITUATION_VALUES.CITIZEN:
          case LHC_SITUATION_VALUES.MIGRANT:
            // reuse `dateTypeText` declaration for condition instead of declare new one for optimisation
            dateTypeText = this.lhc.situation === LHC_SITUATION_VALUES.MIGRANT;
            dateTypeText = dateTypeText ? 'the Medicare registration' : 'your overseas return';

            if (input.DATE_VALIDATOR.$invalid) {
              return `Please enter ${dateTypeText} date using the format DD/MM/YYYY.`;
            }

            if (input.isPastDate.$invalid) {
              return 'Please enter a date in the past.';
            }

            return `Please provide ${dateTypeText}.`;

          case LHC_SITUATION_VALUES.EX_ADF:
            dateTypeText = dateType === 'enlist' ? 'enlistment' : 'discharge';
            // common
            if (input.DATE_VALIDATOR.$invalid) {
              return `Please enter the ${dateTypeText} date using the format DD/MM/YYYY.`;
            }

            if (input.required.$invalid) {
              return `Please include a response for date of ${dateTypeText}.`;
            }

            // for enlist date
            if (dateType === 'enlist') {
              if (input.overPastDate.$invalid) {
                return 'Enlistment date cannot be more than 109 years ago.';
              }

              if (input.isPastDateExcludingToday.$invalid) {
                return 'Enlistment date must be a past date.';
              }
            }

            // for discharge date
            if (dateType === 'discharge') {
              if (input.lessThan90Days.$invalid) {
                return 'We can only accept future dates within 90 days of today.';
              }

              if (input.beforeYear1970.$invalid) {
                return 'Discharge date cannot be earlier than 1970.';
              }

              if (input.beforeEnlistment.$invalid) {
                return 'Discharge date cannot be before enlistment date.';
              }
            }
            break;

          default:
            break;
        }
      }
      return '';
    },

    calculateAge,

    findCdaDropdown() {
      if (this.abd.selectedCdaDropdown !== undefined) {
        const selectedCdaDropdown = this.cdaDropdownOptions.find((obj) => {
          return obj.name === this.abd.selectedCdaDropdown.toString();
        });

        if (selectedCdaDropdown !== undefined) {
          return selectedCdaDropdown.name;
        }
      } else if (this.abd.cda) {
        // if cda in not null, undefined or an empty string, return it as a string so the dropdown can be pre-filled
        return this.abd.cda.toString();
      }
      this.abd.selectedCdaDropdown = undefined; // used for cases where a previous dropdown is saved and needs to be wiped
      return 'Select an age';
    },

    updateCdaValue() {
      this.abd.cda = this.abd.selectedCdaDropdown;
      this.abd.cda = this.currentCDA;
    },

    updateCurrentMember() {
      this.$emit(EMIT_KEYS.CURRENT_MEMBER_UPDATED, this.currentDefenceMember);
    },

    isEmptyString,
  },
};
</script>

<style lang="scss" scoped>
.no-margin-bottom {
  margin-bottom: 0;
}

.inline-message {
  margin: 0px;
}
</style>
